import React, { useState } from 'react';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS } from '../constants/theme';

/**
 * VideoPlayer Component
 * HTML5 video player with responsive sizing and error handling
 *
 * @param {Object} props
 * @param {string} props.videoUrl - Video source URL (signed URL or external)
 * @param {string} props.thumbnailUrl - Poster/thumbnail image URL
 * @param {string} props.title - Video title for accessibility
 * @param {Function} props.onError - Error handler callback
 */
const VideoPlayer = ({ videoUrl, thumbnailUrl = null, title = 'Drill Video', onError = null }) => {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Handle video load
   */
  const handleLoadedMetadata = () => {
    setLoading(false);
    setHasError(false);
  };

  /**
   * Handle video error
   */
  const handleError = (e) => {
    setLoading(false);
    setHasError(true);

    const error = e.target.error;
    let message = 'Unable to load video. ';

    switch (error?.code) {
      case 1: // MEDIA_ERR_ABORTED
        message += 'Video loading was aborted.';
        break;
      case 2: // MEDIA_ERR_NETWORK
        message += 'Network error occurred.';
        break;
      case 3: // MEDIA_ERR_DECODE
        message += 'Video format is not supported.';
        break;
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        message += 'Video format or source is not supported.';
        break;
      default:
        message += 'Please try again later.';
    }

    setErrorMessage(message);

    // Call error callback if provided
    if (onError) {
      onError({ code: error?.code, message });
    }
  };

  /**
   * Handle waiting state
   */
  const handleWaiting = () => {
    setLoading(true);
  };

  /**
   * Handle playing state
   */
  const handlePlaying = () => {
    setLoading(false);
  };

  // Styles
  const containerStyle = {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    position: 'relative',
  };

  const videoContainerStyle = {
    position: 'relative',
    paddingTop: '56.25%', // 16:9 aspect ratio
    backgroundColor: COLORS.gray[900],
    borderRadius: BORDER_RADIUS.md,
    overflow: 'hidden',
  };

  const videoStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };

  const loadingOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: COLORS.white,
    fontSize: FONT_SIZE.md,
    zIndex: 1,
  };

  const errorContainerStyle = {
    padding: SPACING.lg,
    backgroundColor: COLORS.alert.error.bg,
    borderRadius: BORDER_RADIUS.md,
    border: `1px solid ${COLORS.alert.error.border}`,
    textAlign: 'center',
  };

  const errorIconStyle = {
    fontSize: '48px',
    marginBottom: SPACING.md,
  };

  const errorTextStyle = {
    fontSize: FONT_SIZE.md,
    color: COLORS.alert.error.text,
    marginBottom: SPACING.sm,
  };

  const errorDetailStyle = {
    fontSize: FONT_SIZE.sm,
    color: COLORS.gray[600],
  };

  // Show error state
  if (hasError) {
    return (
      <div style={containerStyle}>
        <div style={errorContainerStyle}>
          <div style={errorIconStyle}>⚠️</div>
          <div style={errorTextStyle}>
            <strong>Video Unavailable</strong>
          </div>
          <div style={errorDetailStyle}>{errorMessage}</div>
        </div>
      </div>
    );
  }

  // Show video player
  return (
    <div style={containerStyle}>
      <div style={videoContainerStyle}>
        {/* Loading overlay */}
        {loading && (
          <div style={loadingOverlayStyle}>
            <div>Loading video...</div>
          </div>
        )}

        {/* Video element */}
        {videoUrl && (
          <video
            style={videoStyle}
            controls
            controlsList="nodownload" // Disable download button (Chrome/Edge)
            poster={thumbnailUrl || undefined}
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleError}
            onWaiting={handleWaiting}
            onPlaying={handlePlaying}
            preload="metadata"
            playsInline // Better mobile support
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/webm" />
            <source src={videoUrl} type="video/quicktime" />
            <track kind="captions" />
            <p style={{ color: COLORS.white, padding: SPACING.md }}>
              Your browser does not support the video tag. Please try viewing in a different
              browser.
            </p>
          </video>
        )}
      </div>

      {/* Video title (for accessibility and context) */}
      {title && (
        <div
          style={{
            marginTop: SPACING.sm,
            fontSize: FONT_SIZE.sm,
            color: COLORS.gray[600],
            textAlign: 'center',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
